import { useContext, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../../../../../store/common/TypedSelector';
import { AuthContext } from '../../../../../core/context/AuthContext';
import { ClientContext } from '../../../../../core/context/ClientContext';
import { FacilityContext } from '../../../../../core/context/FacilityContext';

import { ContainerViewModel } from '../../../../domain/model/ContainerModel';
import { ContainerModelState } from '../../../../domain/model/ContainerModelState';
import { CyclotronProductViewModel } from '../../../../domain/model/CyclotronProductModel';
import { GeneratorEluateIsotope } from '../../../../domain/model/isotopes/GeneratorEluateIsotope';
import { GeneratorTypeIsotope } from '../../../../domain/model/isotopes/GeneratorTypeIsotope';
import { NuclideHalfLifePerHour } from '../../../../domain/model/isotopes/NuclideHalfLifePerHour';
import { calculateRemainingTime } from '../../../../domain/model/isotopes/NuclideIsotopeDefinition';
import { NuclideSpecificActivity } from '../../../../domain/model/isotopes/NuclideSpecificActivity';
import { updateContainer } from '../../../../store/containerSlice';
import { selectInWastemanagementCyclotronProducts } from '../../../../store/cyclotronProductSlice';
import { selectInWastemanagementEluates } from '../../../../store/eluateSlice';
import { selectGenerators } from '../../../../store/generatorSlice';
import { EluatePayloadType } from '../../../view/SingleNuclidesView';
import { NuclideHalflife } from '../container-factor-measurement-form/ContainerFactorMeasurementForm';
import { ContainerStationMeasurementFormStep1 } from './ContainerStationMeasurementFormStep1';
import { ContainerStationMeasurementFormStep2 } from './ContainerStationMeasurementFormStep2';

export interface ContainerStationMeasurementFormProps {
	container?: ContainerViewModel;
}

export const ContainerStationMeasurementForm = (props: ContainerStationMeasurementFormProps): JSX.Element => {
	const { container } = props;

	const authContext = useContext(AuthContext);
	const clientContext = useContext(ClientContext);
	const facilityContext = useContext(FacilityContext);

	// Consume the dispatch object
	const dispatch = useDispatch();

	// Local state
	const [showStep1, setShowStep1] = useState<boolean>(true);
	const [invalidInputs, setInvalidInputs] = useState<string[]>([]);

	const updatedContainer = useRef<ContainerViewModel>({} as ContainerViewModel);

	const inWastemanagementCyclotronProducts = useTypedSelector(selectInWastemanagementCyclotronProducts(
		clientContext.selectedClientUuid,
		facilityContext.selectedFacilityUuid
	));

	const inWastemanagementEluates = useTypedSelector(selectInWastemanagementEluates());

	const generators = useTypedSelector(selectGenerators(
		clientContext.selectedClientUuid,
		facilityContext.selectedFacilityUuid
	));

	inWastemanagementEluates.forEach(eluate => {

		const inWastemanagementEluateGenerator = generators.find((generator) => {
			return generator.Uuid === eluate.Generator;
		});
		if (inWastemanagementEluateGenerator.Isotope === GeneratorTypeIsotope.MOLYBDENUM_99_MO) {
			eluate.Isotope = GeneratorEluateIsotope.TECHNETIUM_99M_TC;
		} else {
			eluate.Isotope = GeneratorEluateIsotope.GALLIUM_68_GA;
		}
	});

	const inWastemanagementNuclides: Array<EluatePayloadType | CyclotronProductViewModel> =
		[].concat(inWastemanagementEluates, inWastemanagementCyclotronProducts);

	let containerNuclideIds: string[] = [];
	containerNuclideIds = [].concat(container.GeneratorEluatIds, container.CyclotroneProductIds);

	const containerNuclides = inWastemanagementNuclides.filter(nuclideModel => containerNuclideIds.includes(nuclideModel.Uuid));

	const requiredInputs = ['MeasurementDate', 'MeasurementActivity', 'Weight'];

	let currentSpecificActivity:number = 0;
	currentSpecificActivity = updatedContainer.current.MeasurementActivity / updatedContainer.current.Weight;

	let specificActivity: any;
	let halflife: number;
	const checkContainerNuclides = containerNuclides.every((nuclide) => {
		return nuclide.Isotope === containerNuclides[0].Isotope;
	});

	if (checkContainerNuclides && containerNuclides[0].Isotope === GeneratorEluateIsotope.TECHNETIUM_99M_TC) {
		specificActivity = NuclideSpecificActivity.TECHNETIUM_99M_TC;
		halflife = NuclideHalfLifePerHour.TECHNETIUM_99M_TC;
	} if (checkContainerNuclides && containerNuclides[0].Isotope !== GeneratorEluateIsotope.TECHNETIUM_99M_TC) {
		specificActivity = NuclideSpecificActivity[containerNuclides[0].Isotope];
		halflife = NuclideHalfLifePerHour[containerNuclides[0].Isotope];
	}  if (!checkContainerNuclides) {
		const nuclidesHalfLifes: NuclideHalflife[] = [];
		containerNuclides.map((item) => {
			const nuclideHalflifeObject: NuclideHalflife = {
				Nuclide: item.Isotope,
				Halflife: NuclideHalfLifePerHour[item.Isotope]
			};
			nuclidesHalfLifes.push(nuclideHalflifeObject);
			const usedNuclideMixedContainer = nuclidesHalfLifes.sort((a, b) => a.Halflife - b.Halflife).reverse()[0].Nuclide;
			specificActivity = NuclideSpecificActivity[usedNuclideMixedContainer];
			halflife = NuclideHalfLifePerHour[usedNuclideMixedContainer];
			return specificActivity;
		});
	}

	const remainingTime = calculateRemainingTime(halflife, currentSpecificActivity, specificActivity);

	const addHours = (date: Date, hours: number): Date => {
		const result = new Date(date);
		result.setHours(result.getHours() + hours);
		return result;
	};

	const releaseDate = addHours(updatedContainer.current.MeasurementDate, remainingTime);

	const onChangeContainerProp = (prop: string, value: number | Date) => {
		updatedContainer.current[prop] = value;
	};

	if ((updatedContainer.current?.MeasurementDate ?? null) === null) {
		updatedContainer.current.MeasurementDate = new Date();
	}

	const handleUpdate = (): void => {
		updatedContainer.current.Client = clientContext.selectedClientUuid;
		updatedContainer.current.Facility = facilityContext.selectedFacilityUuid;
		updatedContainer.current.Uuid = container.Uuid;
		updatedContainer.current.State = ContainerModelState.DECAY;
		updatedContainer.current.ReleaseDateTime = releaseDate;
		updatedContainer.current.MeasurementActivity = currentSpecificActivity;
		updatedContainer.current.MeasuredBy = authContext.getActor().Uuid;
		dispatch(updateContainer(updatedContainer.current));
	};

	const goToStep1 = () => {
		setShowStep1(true);
		setInvalidInputs([]);
	};

	const goToStep2 = () => {
		let newContainerModelValid = true;
		const invalidInputFields: Array<string> = [];
		for (const requiredInput of requiredInputs) {
			if (
				(updatedContainer?.current[requiredInput] ?? null) === null
				|| updatedContainer?.current[requiredInput] === ''
				|| String(updatedContainer.current.requiredInput).trim().length === 0
			) {
				invalidInputFields.push(requiredInput);
				newContainerModelValid = false;
			}
		}
		setInvalidInputs(invalidInputFields);
		setShowStep1(!newContainerModelValid);
	};

	const showCreateModalStep = showStep1 === true ?
		<ContainerStationMeasurementFormStep1
			container={updatedContainer.current}
			onClickNext={goToStep2}
			invalidInputs={invalidInputs}
			onChangeContainerProp={onChangeContainerProp}
		/> :
		<ContainerStationMeasurementFormStep2
			container={updatedContainer.current}
			currentSpecificActivity={currentSpecificActivity}
			specificActivity={specificActivity}
			halflife={halflife}
			releaseDateTime={releaseDate}
			onClickPrevious={goToStep1}
			onClickNext={handleUpdate}
		/>;

	return (
		<>
			{showCreateModalStep}
		</>
	);
};
